import "@/components/Layout/BaseLayouts/StickyEditCard.scss";

import clsx from "clsx";
import { ReactNode } from "react";

import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer.tsx";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useFixStickyCardScrollAfterKeyboardClose } from "@/hooks/useFixStickyCardScrollAfterKeyboardClose";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FCCC } from "@/types.ts";

export interface SubscriptionCardProps {
  title: string;
  /**
   * Right-hand control(s) of the header, e.g. the "Ändern" button or a
   * Save/Cancel pair. Multiple elements are laid out with a gap between them.
   */
  action?: ReactNode;
  /**
   * When `true`, pins the card to the viewport on mobile (portal header and
   * bottom nav hidden) so title/actions stay visible while the body scrolls.
   * Defaults to `false` — multi-card SIM tabs must opt in only while editing.
   */
  stickyHeader?: boolean;
  dataTestid?: string;
}

/**
 * An ivory panel whose header carries the section title and a single action,
 * as the detail view uses for "Dein Abo".
 */
export const SubscriptionCard: FCCC<SubscriptionCardProps> = ({
  title,
  action,
  stickyHeader = false,
  dataTestid,
  className,
  children,
}) => {
  const isMobile = useIsMobile();
  useBodyScrollLock(stickyHeader && isMobile);
  const stickyBodyRef = useFixStickyCardScrollAfterKeyboardClose(stickyHeader);

  return (
    <CardContainer
      dataTestid={dataTestid}
      variant="ivory"
      className={clsx(
        "flex w-full flex-col gap-2 rounded-lg p-4",
        stickyHeader && "sticky-edit-card",
        className,
      )}
    >
      <div className="sticky-edit-card__header flex w-full shrink-0 items-start gap-3">
        {/* Title spacing mirrors ProfileCard: only vertical padding so the title
            lines up with the card edge instead of being indented by an extra
            horizontal p-2. */}
        <div className="flex min-w-0 flex-1 flex-col justify-center py-2">
          <p className="truncate-safe w-full text-body-base font-semibold leading-none text-foreground-primary">
            {title}
          </p>
        </div>
        {/* Match ProfileCard's action wrapper so a button pair (e.g. Save/Cancel)
            is vertically centred against the title and spaced apart. */}
        {action && (
          <div className="flex shrink-0 items-center gap-3">{action}</div>
        )}
      </div>
      <div
        ref={stickyBodyRef}
        className="sticky-edit-card__body flex w-full flex-col gap-2"
      >
        {children}
      </div>
    </CardContainer>
  );
};
